<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl mb-1">Invoices</h1>
    <DownloadButton
      title="Download CSV"
      label="Download"
      :downloading="isDownloadingCsv"
      :disabled="invoices.length === 0"
      @click="downloadCsv"
    />
  </div>
  <ErrorDisplay v-if="downloadErrorMessage" :error-message="downloadErrorMessage" />
  <WarningDisplay v-if="showSlowLoadWarning" />
  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <InvoicesControls
      @update:status="onStatusChange"
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />

    <DataTable
      :is-loading="isLoading"
      :columns="invoicesTableColumns"
      :data="invoices"
      :total-count="totalCount"
      :page-number="pageNumber"
      @update:page="onUpdatePage"
      @update:page-size="onUpdatePageSize"
    />
  </div>
</template>

<script setup lang="ts">
import DownloadButton from '@/components/common/DownloadButton.vue';
import ErrorDisplay from '@/components/common/ErrorDisplay.vue';
import WarningDisplay from '@/components/common/WarningDisplay.vue';
import { fetchInvoices, type InvoiceServiceGetManyRequest } from '@/services/invoices';
import { useGeneralStore } from '@/stores/general';
import { useUserStore } from '@/stores/users';
import { handleError } from '@/utils/error.util';
import { fetchAllPaginated, generateCsvBlobUrl } from '@/utils/export.utils';
import { addMetric } from '@/utils/monitoring.utils';
import { deepEqual } from '@/utils/paging.utils';
import dayjs from 'dayjs';
import type { DateRange } from 'radix-vue';
import { computed, onMounted, ref, watch } from 'vue';
import type { ModelInvoice } from '@layup/api-sdk';
import { formatDateShort, toRandString } from '../../../../utils/format.utils';
import DataTable from '../../DataTable.vue';
import InvoicesControls from '../invoices-controls/InvoicesControls.vue';
import { invoicesTableColumns } from './InvoicesTableColumns';

const generalStore = useGeneralStore();

const isLoading = ref(true);
const isDownloadingCsv = ref(false);
const downloadErrorMessage = ref('');
const invoiceParams = ref<InvoiceServiceGetManyRequest>({});
const previousParams = ref<InvoiceServiceGetManyRequest>({ ...invoiceParams.value });
const invoices = ref<Partial<ModelInvoice>[]>([]);

const totalCount = ref(1);
const pageNumber = ref(1);
const showLoadWarning = ref(false);
const SLOW_LOAD_WARNING_DELAY_MS = 3000;
let slowLoadWarningTimeout: ReturnType<typeof setTimeout> | null = null;
const showSlowLoadWarning = computed(() => showLoadWarning.value);

watch(
  () => isLoading.value || isDownloadingCsv.value,
  (inProgress) => {
    if (inProgress) {
      showLoadWarning.value = false;
      if (slowLoadWarningTimeout) {
        clearTimeout(slowLoadWarningTimeout);
      }
      slowLoadWarningTimeout = setTimeout(() => {
        if (isLoading.value || isDownloadingCsv.value) {
          showLoadWarning.value = true;
        }
      }, SLOW_LOAD_WARNING_DELAY_MS);
      return;
    }

    if (slowLoadWarningTimeout) {
      clearTimeout(slowLoadWarningTimeout);
      slowLoadWarningTimeout = null;
    }
    showLoadWarning.value = false;
  },
  { immediate: true },
);

onMounted(async () => {
  await onUpdateFilter(invoiceParams.value, true);
  await useUserStore().getSessionExpiry();
});

const onUpdateFilter = async (params: InvoiceServiceGetManyRequest, skipCheck: boolean = false) => {
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  invoices.value = [];
  isLoading.value = true;

  try {
    params.limit = generalStore.pageSize;
    params.skip = (pageNumber.value - 1) * generalStore.pageSize;
    const response = await fetchInvoices(params);
    invoices.value = response.invoices || [];
    totalCount.value = response.totalCount || 0;
    addMetric('invoices-table', { status: 'success', channel: 'merchant' });
  } catch (error) {
    handleError(error, { metricName: 'invoices-table' });
  } finally {
    isLoading.value = false;
  }
};

const onStatusChange = (value: string): void => {
  invoiceParams.value.status = value;
  onUpdateFilter(invoiceParams.value);
};

const onDateRangeChange = (value: DateRange): void => {
  invoiceParams.value.from = `${value.start?.year}-${value.start?.month}-${value.start?.day}`;
  invoiceParams.value.to = `${value.end?.year}-${value.end?.month}-${value.end?.day}`;
  onUpdateFilter(invoiceParams.value);
};

const onClearFilters = (): void => {
  pageNumber.value = 1;
  invoiceParams.value = { skip: 0 };
  onUpdateFilter(invoiceParams.value);
};

const onSearchTermChange = (value: string): void => {
  invoiceParams.value.reference = value.toUpperCase();
  onUpdateFilter(invoiceParams.value);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
  invoiceParams.value.skip = (pageNumber.value - 1) * generalStore.pageSize;
  onUpdateFilter(invoiceParams.value);
};

const onUpdatePageSize = (size: number) => {
  pageNumber.value = 1;
  generalStore.pageSize = size;
  invoiceParams.value.limit = size;
  onUpdateFilter(invoiceParams.value);
};

const downloadCsv = async () => {
  isDownloadingCsv.value = true;
  downloadErrorMessage.value = '';
  try {
    const invoices = await fetchAllPaginated<ModelInvoice, InvoiceServiceGetManyRequest>(
      fetchInvoices,
      invoiceParams.value,
      'invoices',
    );

    if (invoices.length === 0) {
      console.warn('No data available for export');
      return;
    }
    const headerMap: Partial<Record<keyof ModelInvoice, string>> = {
      createdAt: 'Created Date',
      accountingInvoiceNumber: 'Invoice Number',
      totalAmount: 'Total Amount',
      reference: 'Reference',
      status: 'Status',
    };
    const data = invoices.map((invoice) => {
      return {
        ...invoice,
        createdAt: invoice.createdAt ? formatDateShort(invoice.createdAt) : '',
        totalAmount: toRandString(invoice.totalAmount ?? 0),
      };
    });

    const url = generateCsvBlobUrl(headerMap, data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoices-${dayjs().format('YYYY-MM-DD')}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    downloadErrorMessage.value = handleError(error, {
      metricName: 'invoices-table-csv-download',
      networkMessage:
        'Network error occurred while downloading invoices CSV. Please check your connection and try again.',
      unexpectedMessage: 'An unexpected error occurred while downloading invoices CSV. Please try again later.',
    });
  } finally {
    isDownloadingCsv.value = false;
  }
};
</script>
